import { api } from './api';

// Types
export interface AgentStats {
  totalSales: number;
  totalRevenue: number;
  totalOrders?: number;
  todaySales?: number;
  thisWeekSales?: number;
  thisMonthSales?: number;
  growthRate?: number;
  activeCustomers: number;
  pendingOrders: number;
  monthlyGrowth: number;
  commissionEarned: number;
}

export interface AgentOrder {
  id: string;
  customer: string;
  customerEmail: string;
  customerPhone: string;
  package: string;
  network: 'mtn' | 'airteltigo' | 'telecel';
  amount: number;
  commission: number;
  status: 'pending' | 'completed' | 'failed' | 'processing';
  date: string;
  paymentMethod: string;
  notes?: string;
}

export interface AgentCustomer {
  id: string;
  name: string;
  email: string;
  phone: string;
  walletBalance?: number;
  currency?: string;
  totalOrders: number;
  completedOrders?: number;
  totalSpent: number;
  totalEarnings?: number;
  profitMargin?: number;
  lastOrderDate: string | null;
  status: 'active' | 'inactive';
  preferredNetwork: string;
  createdAt: string;
}

export interface AgentProduct {
  id: string;
  name: string;
  network: 'mtn' | 'airteltigo' | 'telecel';
  size: string;
  validity: string;
  price: number;
  commissionRate: number;
  sales: number;
  revenue: number;
  growth: number;
  isActive: boolean;
}

export interface CreateOrderData {
  customerId: string;
  packageId: string;
  customerPhone: string;
  customerEmail?: string;
  notes?: string;
  network: 'mtn' | 'airteltigo' | 'telecel';
}

export interface CreateCustomerData {
  name: string;
  email: string;
  phone: string;
  preferredNetwork?: string;
  notes?: string;
}

export interface AgentReport {
  period: string;
  totalSales: number;
  totalRevenue: number;
  totalCommission: number;
  ordersByStatus: Record<string, number>;
  topProducts: AgentProduct[];
  revenueByNetwork: Record<string, number>;
  customerGrowth: number;
  averageOrderValue: number;
}

// Agent API Service
export const agentAPI = {
  // Dashboard Stats
  getStats: (timeRange?: string) =>
    api.get<{ data: AgentStats }>('/agent/stats', { 
      params: timeRange ? { timeRange } : {} 
    }),

  // Orders Management
  getOrders: (params?: {
    page?: number;
    limit?: number;
    status?: string;
    search?: string;
    dateFrom?: string;
    dateTo?: string;
  }) =>
    api.get<{ data: AgentOrder[], pagination: any }>('/agent/orders', { params }),

  getOrder: (id: string) =>
    api.get<{ data: AgentOrder }>(`/agent/orders/${id}`),

  createOrder: (orderData: CreateOrderData) =>
    api.post<{ data: AgentOrder }>('/agent/orders', orderData),

  updateOrder: (id: string, updateData: Partial<AgentOrder>) =>
    api.patch<{ data: AgentOrder }>(`/agent/orders/${id}`, updateData),

  cancelOrder: (id: string, reason?: string) =>
    api.post<{ data: AgentOrder }>(`/agent/orders/${id}/cancel`, { reason }),

  // Customer Management
  getCustomers: (params?: {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
  }) =>
    api.get<{ data: AgentCustomer[], pagination: any }>('/agent/customers', { params }),

  getCustomer: (id: string) =>
    api.get<{ data: AgentCustomer }>(`/agent/customers/${id}`),

  createCustomer: (customerData: CreateCustomerData) =>
    api.post<{ data: AgentCustomer }>('/agent/customers', customerData),

  updateCustomer: (id: string, updateData: Partial<CreateCustomerData>) =>
    api.patch<{ data: AgentCustomer }>(`/agent/customers/${id}`, updateData),

  getCustomerOrders: (customerId: string, params?: {
    page?: number;
    limit?: number;
    status?: string;
  }) =>
    api.get<{ data: AgentOrder[], pagination: any }>(`/agent/customers/${customerId}/orders`, { params }),

  // Products & Performance
  getTopProducts: (timeRange?: string) =>
    api.get<{ data: AgentProduct[] }>('/agent/top-products', { 
      params: timeRange ? { timeRange } : {} 
    }),

  getProducts: (params?: {
    network?: string;
    active?: boolean;
  }) =>
    api.get<{ data: AgentProduct[] }>('/agent/products', { params }),

  // Reports & Analytics
  getSalesReport: (params: {
    period: string;
    format?: 'json' | 'csv' | 'pdf';
  }) =>
    api.get<{ data: AgentReport }>('/agent/reports/sales', { params }),

  getCommissionReport: (params: {
    period: string;
    format?: 'json' | 'csv' | 'pdf';
  }) =>
    api.get<{ data: any }>('/agent/reports/commission', { params }),

  exportReport: (type: string, params: {
    period: string;
    format: 'csv' | 'pdf';
  }) =>
    api.get(`/agent/reports/${type}/export`, { 
      params,
      responseType: 'blob'
    }),
  
  generateReport: (params: {
    startDate: string;
    endDate: string;
    format: 'pdf' | 'csv' | 'excel';
  }) =>
    api.get('/agent/report', { params }),

  // Wallet & Commission
  getCommissionStats: () =>
    api.get<{ data: {
      totalEarned: number;
      thisMonth: number;
      lastMonth: number;
      pending: number;
      paid: number;
      rate: number;
    } }>('/agent/commission/stats'),

  getCommissionHistory: (params?: {
    page?: number;
    limit?: number;
    status?: string;
  }) =>
    api.get<{ data: any[], pagination: any }>('/agent/commission/history', { params }),

  requestCommissionPayout: (payload: {
    amount: number;
    method: 'wallet' | 'mobile_money';
    phoneNumber?: string;
    mobileNetwork?: 'mtn' | 'airteltigo' | 'telecel';
  }) =>
    api.post<{ data: any }>('/agent/commission/payout', payload),

  // Quick Actions
  quickTopup: (data: {
    phone: string;
    packageId: string;
    amount: number;
  }) =>
    api.post<{ data: AgentOrder }>('/agent/quick-topup', data),

  validatePhoneNumber: (phone: string) =>
    api.post<{ data: { valid: boolean, network: string } }>('/agent/validate-phone', { phone }),

  // Notifications
  getNotifications: (params?: {
    page?: number;
    limit?: number;
    read?: boolean;
  }) =>
    api.get<{ data: any[], pagination: any }>('/agent/notifications', { params }),

  markNotificationRead: (id: string) =>
    api.patch<{ data: any }>(`/agent/notifications/${id}/read`),

  // Settings
  getSettings: () =>
    api.get<{ data: any }>('/agent/settings'),

  updateSettings: (settings: any) =>
    api.patch<{ data: any }>('/agent/settings', settings),

  // Payout Accounts (Admin-Approved MoMo accounts)
  getPayoutAccounts: () =>
    api.get<{ success: boolean; data: AgentPayoutAccount[] }>('/agent/payout-accounts'),

  submitPayoutAccount: (data: { phoneNumber: string; accountName: string; network: string }) =>
    api.post<{ success: boolean; data: AgentPayoutAccount; message: string }>('/agent/payout-accounts', data),

  deletePayoutAccount: (id: string) =>
    api.delete<{ success: boolean; message: string }>(`/agent/payout-accounts/${id}`),
};

export interface AgentPayoutAccount {
  _id: string;
  userId: string;
  phoneNumber: string;
  accountName: string;
  network: 'mtn' | 'telecel' | 'airteltigo';
  status: 'pending' | 'approved' | 'rejected';
  rejectionReason?: string;
  submittedAt: string;
  approvedAt?: string;
}

// Helper functions for data formatting
export const formatAgentData = {
  formatOrderStatus: (status: string) => {
    const statusMap = {
      pending: { label: 'Pending', color: 'warning' },
      completed: { label: 'Completed', color: 'success' },
      failed: { label: 'Failed', color: 'danger' },
      processing: { label: 'Processing', color: 'info' },
    };
    return statusMap[status as keyof typeof statusMap] || { label: status, color: 'gray' };
  },

  formatNetworkColor: (network: string) => {
    const colors = {
      mtn: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      airteltigo: 'bg-blue-100 text-blue-800 border-blue-200',
      telecel: 'bg-green-100 text-green-800 border-green-200',
    };
    return colors[network as keyof typeof colors] || 'bg-gray-100 text-gray-800 border-gray-200';
  },

  calculateCommission: (amount: number, rate: number) => {
    return (amount * rate) / 100;
  },

  formatGrowthIndicator: (growth: number) => {
    return {
      value: Math.abs(growth),
      isPositive: growth > 0,
      isNegative: growth < 0,
      trend: growth > 0 ? 'up' : growth < 0 ? 'down' : 'stable'
    };
  },
};

export default agentAPI;
